import { Link } from "react-router-dom";
import { ArrowRight, Ship, TriangleAlert, Layers, CheckCircle2, Zap } from "lucide-react";
import { AvailabilityBlock } from "@/components/Availability";

const HERO_IMG = "https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg";
const FEATURE_IMG = "https://images.unsplash.com/photo-1764795850513-fa8a469a4600";
const SHIP_IMG = "https://images.unsplash.com/photo-1605745341112-85968b19335b";

const Nav = () => (
  <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
    <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
      <div className="flex items-center gap-2">
        <span className="grid h-7 w-7 place-items-center bg-slate-900 text-[11px] font-bold text-white">S3</span>
        <span className="font-display text-base font-semibold">Stock360</span>
      </div>
      <nav className="hidden items-center gap-7 text-sm text-slate-600 md:flex">
        <a href="#how" className="transition-colors hover:text-slate-900" data-testid="marketing-nav-how">How it works</a>
        <a href="#gap" className="transition-colors hover:text-slate-900" data-testid="marketing-nav-gap">Gap engine</a>
        <a href="#pricing" className="transition-colors hover:text-slate-900" data-testid="marketing-nav-pricing">Pricing</a>
      </nav>
      <div className="flex items-center gap-2">
        <Link to="/login" data-testid="marketing-login-link" className="px-3 py-2 text-sm text-slate-600 transition-colors hover:text-slate-900">
          Sign in
        </Link>
        <Link
          to="/register"
          data-testid="marketing-trial-button"
          className="bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          Start 14-day trial
        </Link>
      </div>
    </div>
  </header>
);

const PRICING = [
  { name: "Starter", price: "£15", locations: "1 location", features: ["Unlimited users", "Xero invoice commitment", "Gap engine", "Excel uploads"] },
  { name: "Growth", price: "£35", locations: "Up to 3 locations", features: ["Everything in Starter", "Transfers & bins", "Incoming shipments", "Landed costs"], highlight: true },
  { name: "Importer", price: "£69", locations: "Up to 10 locations", features: ["Everything in Growth", "Serial / batch & expiry", "Gap history reporting", "Priority support"] },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      <Nav />

      <section className="grid-paper border-b border-slate-200">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 py-16 md:py-24 lg:grid-cols-12">
          <div className="rise lg:col-span-7">
            <span className="inline-flex items-center gap-2 border border-slate-300 px-2.5 py-1 text-[11px] uppercase tracking-widest text-slate-600">
              <Zap className="h-3 w-3" /> Built for UK importers on Xero
            </span>
            <h1 className="mt-6 font-display text-4xl font-semibold leading-[1.05] sm:text-5xl lg:text-6xl">
              Xero raises the invoice.
              <br />
              <span className="text-slate-400">Stock360 knows the stock.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-600">
              Stock commits the moment an invoice is authorised — never at quote. See On Hand,
              Committed, Available and Incoming on every screen, track containers still on the
              water, and get told which substitute covers the shortfall.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/register"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-slate-900 px-5 py-3 text-sm font-medium text-white transition-colors hover:bg-slate-700"
              >
                Start free — no card <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-900 hover:text-white"
              >
                See the live commit demo
              </Link>
            </div>
            <p className="mt-4 text-xs text-slate-500">
              GBP-only ledger · pence-accurate moving average cost · unlimited users on every plan
            </p>
          </div>

          <div className="rise lg:col-span-5" style={{ animationDelay: "120ms" }}>
            <div className="border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
                <span className="mono text-xs text-slate-500">KTL-1500-BLK</span>
                <span className="bg-amber-50 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-700">
                  Committing INV-1042
                </span>
              </div>
              <AvailabilityBlock
                data={{ on_hand: 240, committed: 60, available: 180, incoming: 200 }}
                testIdPrefix="hero-avail"
              />
              <div className="space-y-2 px-4 py-3 text-xs text-slate-600">
                <p className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Invoice authorised in Xero → stock committed in 1.2s</p>
                <p className="flex items-center gap-2"><Ship className="h-3.5 w-3.5 text-violet-600" /> 200 units on the water · ETA 14 Sep</p>
                <p className="flex items-center gap-2"><TriangleAlert className="h-3.5 w-3.5 text-amber-600" /> KTL-1700-SS covers 96 units at 1:1</p>
              </div>
            </div>
            <img src={HERO_IMG} alt="Warehouse racking" className="mt-4 h-44 w-full border border-slate-200 object-cover" />
          </div>
        </div>
      </section>

      <section id="how" className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <h2 className="font-display text-3xl font-semibold">Connect → upload → watch it commit</h2>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Under ten minutes from sign-up to live stock. No double-counting: your Xero items stay
          untracked, Stock360 posts the adjustment and COGS journals.
        </p>
        <div className="mt-10 grid gap-px border border-slate-200 bg-slate-200 md:grid-cols-3">
          {[
            { n: "01", t: "Connect Xero", d: "OAuth in one click. We detect tracked items and guide the switch to untracked." },
            { n: "02", t: "Import & upload", d: "Items, contacts and tax rates in. Opening stock via Excel/CSV with per-row validation." },
            { n: "03", t: "Watch it commit", d: "Authorise an invoice in Xero and see Committed and Available move in real time." },
          ].map((s) => (
            <div key={s.n} className="bg-white p-6">
              <span className="mono text-xs text-slate-400">{s.n}</span>
              <h3 className="mt-3 font-display text-lg font-semibold">{s.t}</h3>
              <p className="mt-2 text-sm text-slate-600">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="gap" className="border-y border-slate-200 bg-[#F8FAFC]">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 md:py-24 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <h2 className="font-display text-3xl font-semibold">The gap engine tells you what to sell instead</h2>
            <p className="mt-4 text-sm text-slate-600">
              Every shortfall shows its cause, the ETA of anything inbound, and the ratio-adjusted
              cover from each alternative — ordered by priority. One click raises the PO.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {[
                "Oversold and below-reorder-point causes separated",
                "Bundle gap = worst component gap, in buildable units",
                "Event-driven recalculation on every stock movement",
                "Daily digest of uncovered shortfalls",
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="grid gap-4 lg:col-span-6">
            <img src={SHIP_IMG} alt="Container ship" className="h-48 w-full border border-slate-200 object-cover" />
            <div className="grid grid-cols-2 gap-4">
              <img src={FEATURE_IMG} alt="Stock check on tablet" className="h-40 w-full border border-slate-200 object-cover" />
              <div className="border border-slate-200 bg-white p-4">
                <Layers className="h-5 w-5 text-slate-400" />
                <p className="mt-3 font-display text-sm font-semibold">Kits & bundles</p>
                <p className="mt-1 text-xs text-slate-600">
                  Components commit individually; the bundle reports how many you can actually build.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <h2 className="font-display text-3xl font-semibold">Priced by locations, not per seat</h2>
        <p className="mt-3 text-sm text-slate-600">14-day trial, no card. Unlimited users on every plan.</p>
        <div className="mt-10 grid gap-px border border-slate-200 bg-slate-200 md:grid-cols-3">
          {PRICING.map((p) => (
            <div key={p.name} className={`bg-white p-6 ${p.highlight ? "ring-2 ring-inset ring-slate-900" : ""}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                {p.highlight && <span className="bg-slate-900 px-2 py-0.5 text-[10px] uppercase tracking-widest text-white">Popular</span>}
              </div>
              <p className="mt-4"><span className="num text-3xl font-semibold">{p.price}</span><span className="text-sm text-slate-500">/month</span></p>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">{p.locations}</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-600">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{f}</li>
                ))}
              </ul>
              <Link
                to="/register"
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                className="mt-6 block bg-slate-900 px-4 py-2.5 text-center text-sm font-medium text-white transition-colors hover:bg-slate-700"
              >
                Start trial
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-4 text-xs text-slate-500">Billing checkout is not connected in this build.</p>
      </section>

      <footer className="border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-8 text-xs text-slate-500 sm:flex-row sm:justify-between">
          <p>© {new Date().getFullYear()} Stock360 — UK inventory tracking for Xero.</p>
          <p>Xero is a trademark of Xero Limited. Stock360 is an independent companion app.</p>
        </div>
      </footer>
    </div>
  );
}
